import MinimumOrderValueChecker from './../shop_settings/minimum_order_value/MinimumOrderValueChecker';
import NetworkGateway from './../shop_settings/minimum_order_value/NetworkGateway';
import TaggedLogger from '../debug/tagged-logger';
/**
 * Factory that creates a function intended for
 * ShopgateProductDetailsCore.registerPlugin.
 */
class AmazonPayUIFactory {
  /**
   * @param {Object}   button - jQuery element
   * @param {string}   url - endpoint for checking minimum order value info
   * @param {function} bindButtonHandler (optional)
   * @param {TaggedLogger|undefined} logger Logger instance
   */
  constructor(button, url, bindButtonHandler, logger = new TaggedLogger('AmazonPayUI')) {
    this.button = button;
    this.url = url;
    this.bindButtonHandler = bindButtonHandler;
    this.logger = logger;

    this.namespace = 'AmazonPayButton';
  }

  /**
   * @return {function} - intended for ShopgateProductDetailsCore.registerPlugin
   */
  getPlugin() {
    const that = this;
    return (productDetailsCore) => {
      const minimumValueChecker = new MinimumOrderValueChecker(
        new NetworkGateway($.shopgate.ajax),
        that.url,
        window.localStorage
      );

      /**
       * Check if the minimum cart value is reached,
       * and set state based on that.
       *
       * @param {int} quantity - how many pieces of the item are selected
       * @param {int|string} amountDisplay - price of single item; e.g., '100' for 1€
       */
      const checkMinimumAmount = (quantity, amountDisplay) => {
        this.logger.info('checkMinimumAmount start');
        // If amountDisplay is missing or 0 - do not block the user
        if (!amountDisplay) {
          this.logger.info('amountDisplay is falsy. Aborting check.');
          productDetailsCore.setState('minimumCartValueReached', true, true);
          return;
        }

        minimumValueChecker.test(quantity, amountDisplay).then((isEnough) => {
          this.logger.info('Minimum cart value reached?', isEnough);
          productDetailsCore.setState('minimumCartValueReached', isEnough, true);
        }).catch((e) => {
          // In case of error, don't block the access to Amazon.
          productDetailsCore.setState('minimumCartValueReached', true, true);
          this.logger.error(e);
        });
      };

      /**
       * @see SGProductDetailsCore
       */
      const onUpdateCartProductQuantity = (callType, data) => {
        let amountDisplay = null;
        const selectedProduct = productDetailsCore.getSelectedProduct();
        if (selectedProduct.product && selectedProduct.product.unit_amount_display) {
          amountDisplay = selectedProduct.product.unit_amount_display;
        }
        checkMinimumAmount(data.quantity, amountDisplay);
      };

      /**
       * Set default state for minimum cart value (which will be used if
       * user clicks on the button before we have data from the backend),
       * and trigger fetching the minimum order value info.
       */
      const initMinimumCartValue = (params) => {
        /**
         * By default, assume that minimum cart value is reached,
         * so if user clicks the button before we have the data from the backend,
         * he isn't blocked.
         */
        productDetailsCore.setState('minimumCartValueReached', true, true);

        let amountDisplay = null;
        if (params.products.parent && params.products.parent.product) {
          amountDisplay = params.products.parent.product.unit_amount_display;
        }

        // Assume that initial quantity is 1.
        checkMinimumAmount(1, amountDisplay);
      };

      /**
       * @see SGProductDetailsCore
       */
      const onCoreInit = (callType, params) => {
        initMinimumCartValue(params);
      };

      /**
       * Disable Pay-With-Amazon-Button if product is not orderable
       * @param  {string} callType The type of the function call (products.ui or products.data)
       * @param  {Object} currentStates The current core states
       */
      function onUpdateStates(callType, currentStates) {
        if (typeof currentStates.cartButtonLocked !== 'undefined') {
          that.button.toggleClass('locked', currentStates.cartButtonLocked);
        }

        if (typeof currentStates.minimumCartValueReached !== 'undefined') {
          that.button.off(`click.${that.namespace}`);
          if (currentStates.minimumCartValueReached === false) {
            that.button.on(`click.${that.namespace}`, (e) => {
              e.preventDefault();
              minimumValueChecker.getMessage().then((message) => {
                const messageObject = {
                  message,
                  state: $.shopgate.systemMessage.options.constants.MESSAGE_ERROR,
                  displayBehavior: $.shopgate
                  .systemMessage
                  .options
                  .constants
                  .DISPLAY_BEHAVIOR_POPUP,
                };
                $(document).trigger('sgFramework.message.system', [messageObject]);
              });
            });
          } else if (typeof that.bindButtonHandler === 'function') {
            that.bindButtonHandler();
          }
        }

        const disableCartButton = productDetailsCore.isAddToCartButtonDisabled();
        that.button.toggleClass('disabled', disableCartButton);
      }

      /**
       * @see SGProductDetailsCore
       */
      function onUpdateProductPriceInfo(callType, data) {
        let quantity = 1;
        if (productDetailsCore.getSelectedValues().quantity) {
          quantity = productDetailsCore.getSelectedValues().quantity;
        }

        const amountDisplay = data.price_info.unit_amount_display;
        checkMinimumAmount(quantity, amountDisplay);
      }

      return {
        vendor: 'Shopgate',
        name: 'ShopgateAmazonPayment',
        type: ['products.ui'],
        onCoreInit,
        onUpdateCartProductQuantity,
        onUpdateStates,
        onUpdateProductPriceInfo,
      };
    };
  }
}

export default AmazonPayUIFactory;



// WEBPACK FOOTER //
// ./Library/1.0/common/amazon-pay/AmazonPayUIFactory.js